import React, { useEffect } from 'react';

import Button from '@amzn/meridian/button';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import useForm from 'src/hooks/useForm';
import JmrService from 'src/services/jmr/jmrService';
import {
  type DownloadTWPAForm,
  type PlacementDetailsAllocatedJobShift,
  TemporaryWorkPlacementEnum,
} from 'src/store/placementDetailsPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { createFileUrl, downloadFile } from 'src/utils/fileUtils';

import GeneralInfo from './generalInfo/generalInfo';
import RadioFields from './radioFields/radioFields';

interface TwpaModalProps {
  placementId: number;
  isTwpaModalOpen: boolean;
  closeTwpaModal: () => void;
  allocatedJobShifts: Array<PlacementDetailsAllocatedJobShift>;
  isJobModificationsEnabledForSite: boolean;
}

/**
 * TWPA Modal
 */
const TwpaModal = ({
  placementId,
  isTwpaModalOpen,
  closeTwpaModal,
  isJobModificationsEnabledForSite,
  allocatedJobShifts,
}: TwpaModalProps) => {
  const { featureFlags } = useAppSelector((state) => state.user);
  const jobModificationsEnabled = featureFlags.JOB_MODIFICATIONS && !!isJobModificationsEnabledForSite;

  // Setup Form Hook
  const {
    handleChange,
    handleChangeDynamicKey,
    validateForm,
    resetFormField,
    resetForm,
    errors: twpaFormErrors,
    formData: twpaFormData,
  } = useForm<DownloadTWPAForm>({
    validations: {
      department: {
        required: { value: true, message: 'Department is required' },
      },
      placementShifts: {
        required: { value: true, message: 'Placement shift(s) is required' },
      },
      reportDate: {
        required: { value: true, message: 'Report date is required' },
      },
      workPlacementProvide: {
        required: {
          value: true,
          message: 'Please select one',
        },
      },
    },
  });

  const getTwpaRadioOptions = () => {
    const radioOptions = [
      {
        value: TemporaryWorkPlacementEnum.MATCH_ROLE_WITH_MODIFICATIONS,
        label: (
          <>
            Restrictions have been matched within your current role. The following modifications will be made in order
            to accommodate your restrictions.
          </>
        ),
      },
      {
        value: TemporaryWorkPlacementEnum.MATCH_JOB,
        label: (
          <>
            Restrictions have been matched with a job transfer to a process path or temporary light duty role. The
            following modifications to the process path or temporary light duty role will be made in order to
            accommodate your restrictions.
          </>
        ),
      },
      {
        value: TemporaryWorkPlacementEnum.NO_MATCH,
        label: (
          <>
            There are no Temporary Work Placements that meet your restrictions available at this time. Associate is
            responsible for calling DLS to initiate a Leave of Absence.
          </>
        ),
      },
    ];

    return [
      ...radioOptions,
      ...(!twpaFormData.jobModifications?.length
        ? [
            {
              value: TemporaryWorkPlacementEnum.MATCH_ROLE_NO_MODIFICATIONS,
              label: (
                <>
                  Restrictions have been matched within your current role. No modifications to the process path are
                  necessary in order to accommodate your restrictions.
                </>
              ),
            },
          ]
        : []),
    ];
  };

  useEffect(() => {
    resetForm();

    const dedupedAppliedModifications = jobModificationsEnabled
      ? [
          ...new Set(allocatedJobShifts?.flatMap((ajs) => ajs.appliedModifications.map((am) => am.modification.name))),
        ].join(', ')
      : '';

    handleChange({ jobModifications: dedupedAppliedModifications });
  }, [isTwpaModalOpen, allocatedJobShifts, jobModificationsEnabled]);

  useEffect(() => {
    if (
      twpaFormData.jobModifications?.length &&
      twpaFormData.workPlacementProvide === TemporaryWorkPlacementEnum.MATCH_ROLE_NO_MODIFICATIONS
    )
      resetFormField('workPlacementProvide');
  }, [twpaFormData.jobModifications]);

  const downloadTWPA = async () => {
    const response = await JmrService.downloadTWPA({
      accommodationId: placementId,
      reportDate: twpaFormData.reportDate,
      placementShift: twpaFormData.placementShifts,
      department: twpaFormData.department,
      notes: twpaFormData.notes,
      jobModifications: twpaFormData.jobModifications,
      workPlacementProvide: TemporaryWorkPlacementEnum[twpaFormData.workPlacementProvide],
      isFullDuty: twpaFormData.isFullDuty, // overwritten by backend
    });

    if (response.data) {
      downloadFile(createFileUrl(response.data, 'application/pdf'), 'twpa-form.pdf');
      resetForm();
      closeTwpaModal();
    }
  };

  return (
    <Modal
      title="Temporary Work Placement Acknowledgement Letter"
      open={isTwpaModalOpen}
      onClose={closeTwpaModal}
      width="680px"
    >
      <GeneralInfo
        twpaFormData={twpaFormData}
        twpaFormErrors={twpaFormErrors}
        handleChange={handleChange}
        jobModificationsEnabled={jobModificationsEnabled}
      />
      <RadioFields
        twpaFormData={twpaFormData}
        twpaFormErrors={twpaFormErrors}
        textInfo={
          <>
            Select which <strong>Temporary Work Placement</strong> will be provided.
          </>
        }
        formDataKey="workPlacementProvide"
        radioOptions={getTwpaRadioOptions()}
        handleChange={handleChangeDynamicKey}
      />
      <Row widths={['fill', '244px']}>
        <div />
        <Row>
          <Button onClick={closeTwpaModal}>Cancel</Button>
          <Button onClick={() => (validateForm() ? downloadTWPA() : null)}>Generate PDF</Button>
        </Row>
      </Row>
    </Modal>
  );
};

// **** Export default **** //

export default TwpaModal;
